import React from 'react';
import PropTypes from 'prop-types';
import {
    colors, borders, radii, space
} from 'style/config';
import {
    Box, Text, Link, Icon
} from 'components/ui';
import { wrapFunctionalComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import { TEXT_TERM_LINK, PRIVACY_POLICY_LINK } from 'constants/legal';
import TextUsEN from 'components/Content/CustomerService/TextUs/locales/TextUs_en_US';
import TextUsFR from 'components/Content/CustomerService/TextUs/locales/TextUs_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(TextUsEN, TextUsFR);

const SMS_PHONE = '1-877-737-4672';
const SMS_URI = 'sms:+18777374672';

/**
 * Customer Service contact-strip tile that lets shoppers start an SMS conversation
 * with Sephora support. Renders the SMS legal disclaimer (with TEXT TERMS / Privacy
 * Policy links), availability hours, and a CTA that opens the native SMS app via the
 * `sms:` URI. Driven by the CMS `CS_textUs` rendering type and shown on Large UI only.
 *
 * @param {Object} props
 * @param {string} props.sid - CMS-provided id applied to the tile's root element.
 * @param {Array|number} [props.marginBottom] - Responsive bottom spacing for the tile.
 * @param {Array|string} [props.gridColumn] - Grid column span; set by LayoutCs so the
 *   contact tiles render as equal-width columns.
 * @returns {JSX.Element|null} The tile, or `null` on Small UI (non-desktop).
 */
const TextUs = ({ sid, marginBottom, gridColumn }) => {
    // This story covers the Large UI (desktop/laptop) breakpoint only.
    // The Small UI experience is handled by a separate story.
    if (!Sephora.isDesktop()) {
        return null;
    }

    return (
        <Box
            id={sid}
            backgroundColor={colors.nearWhite}
            gridColumn={gridColumn}
            borderRadius={2}
            padding={[4, 5]}
            marginBottom={marginBottom}
        >
            <Text
                is='h2'
                fontWeight={'bold'}
                children={getText('textUs')}
                fontSize='xl-bg'
                marginBottom={1}
            />
            <Box marginBottom={3}>
                <Text fontSize='md'>
                    {getText('agreeText')}{' '}
                    <Link
                        href={TEXT_TERM_LINK}
                        target='_blank'
                        rel='noopener noreferrer'
                        color='blue'
                        underline={true}
                        fontWeight={'bold'}
                        children={getText('textTerms')}
                    />{' '}
                    {getText('and')}{' '}
                    <Link
                        href={PRIVACY_POLICY_LINK}
                        target='_blank'
                        rel='noopener noreferrer'
                        color='blue'
                        underline={true}
                        children={getText('privacyPolicy')}
                    />
                    {getText('disclaimer')}
                    <Text
                        is='span'
                        fontWeight={'bold'}
                    >
                        {SMS_PHONE}
                    </Text>
                    .
                </Text>
            </Box>
            <Text fontSize='md'>{getText('representative')}</Text>
            <br />
            <Text
                fontSize='md'
                fontWeight={'bold'}
            >
                {getText('monFri')}:{' '}
            </Text>
            <Text fontSize='md'>{getText('monFriTime')}</Text> <br />
            <Text
                fontSize='md'
                fontWeight={'bold'}
            >
                {getText('satSun')}:{' '}
            </Text>
            <Text fontSize='md'>{getText('satSunTime')}</Text>
            <Box marginTop={4}>
                <Link
                    href={SMS_URI}
                    css={styles.smsButton}
                    aria-label={`${getText('textUs')} ${SMS_PHONE}`}
                >
                    <Icon
                        name='gladChat'
                        size={24}
                        marginRight={2}
                    />
                    <Text
                        fontSize='md'
                        fontWeight={'demiBold'}
                    >
                        {SMS_PHONE}
                    </Text>
                </Link>
            </Box>
        </Box>
    );
};

const styles = {
    smsButton: {
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        padding: space[3],
        borderRadius: radii.full,
        border: `${borders[2]} ${colors.black}`,
        color: colors.black
    }
};

TextUs.propTypes = {
    sid: PropTypes.string.isRequired,
    marginBottom: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
    gridColumn: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
};

export default wrapFunctionalComponent(TextUs, 'TextUs');
